import { supabase } from "@/integrations/supabase/client";

/**
 * True when Supabase already holds a session in this context. Used to decide
 * whether an OAuth attempt actually failed before showing an error.
 */
export async function hasLiveSession(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  } catch {
    return false;
  }
}

/**
 * Turn a raw broker/provider error into something a person can act on.
 * The technical string stays in the console for debugging.
 */
export function mapOAuthError(raw: unknown, provider: "Google" | "Apple"): string {
  const message = raw instanceof Error ? raw.message : typeof raw === "string" ? raw : "";
  // Surface every field the broker gave us — the mapped copy below is
  // intentionally generic, so a repeat failure needs the raw shape to diagnose.
  const detail =
    raw && typeof raw === "object"
      ? Object.fromEntries(
          ["code", "status", "name", "error", "error_description", "error_code", "reason"]
            .map((k) => [k, (raw as Record<string, unknown>)[k]])
            .filter(([, v]) => v !== undefined),
        )
      : {};
  // eslint-disable-next-line no-console
  console.warn(`[${provider} sign-in] ${message || raw}`, detail, raw);
  const lower = message.toLowerCase();
  if (lower.includes("unacceptable audience") || lower.includes("audience") && lower.includes("id_token")) {
    return `Google couldn't complete sign-in. Please try again or use email while the native account setup is updated.`;
  }
  if (lower.includes("exchange authorization code") || lower.includes("invalid_grant")) {
    return `${provider} couldn't complete the secure sign-in exchange. Please try once more; if it continues, use email sign-in.`;
  }
  if (lower.includes("expired")) {
    return `${provider} sign-in expired before it finished — tap Continue with ${provider} again.`;
  }
  if (lower.includes("popup") || lower.includes("blocked") || lower.includes("closed")) {
    return `The ${provider} window closed before sign-in finished — try again.`;
  }
  if (lower.includes("redirect") || lower.includes("origin")) {
    return `${provider} sign-in couldn't return to this page. Open the app in a browser tab and try again.`;
  }
  return message || `${provider} sign-in failed`;
}
